import { FollowType, notEmpty, useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { tracking } from "~/data";
import { getAbsoluteUrl } from "~/lib/utils";
import type { storyPackageQuery$key } from "~/queries/__generated__/storyPackageQuery.graphql";

import {
  Articles,
  Container,
  StyledContentItemHomeStoryPackageLead,
  StyledContentItemHomeStoryPackageTail,
  StyledTopicFollowButton,
  TopicBadge,
  TopicName,
  TopicNameBaseLink,
  TopicNameEntityLink,
} from "./styles";

type Props = {
  reference: storyPackageQuery$key;
};

type Settings = {
  custom_text?: string;
};

export const HomeStoryPackage: FunctionComponent<Props> = ({ reference }) => {
  const data = useFragment(
    graphql`
      fragment storyPackageQuery on Query
      @argumentDefinitions(storyPackageArticlesQueueName: { type: "String!" }) {
        storyPackageArticles: queue(filter: { name: $storyPackageArticlesQueueName }) {
          refTerms {
            ... on Newsletter {
              name
              entityId
              latestArticleLink
              __typename
            }
            ... on Section {
              name
              ...entityLink
              ...followButtonBase
              __typename
            }
            ... on Topic {
              name
              ...entityLink
              ...followButtonBase
              __typename
            }
          }
          settings
          items(first: 3) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...entityLink
                  ...homeStoryPackageLeadContentItemContent
                  ...homeStoryPackageTailContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const listOfArticles = (
    data?.storyPackageArticles?.items?.edges.map(edge => edge.node.entityId) ?? []
  ).join(",");
  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/feature_package",
      category: "Homepage Depth",
    }),
    [],
  );

  const renderTopic = useCallback(() => {
    const terms = data?.storyPackageArticles?.refTerms?.[0];
    const customText = (data?.storyPackageArticles?.settings as Settings)?.custom_text;
    const typename = terms?.__typename;

    if (typename && typename !== "%other") {
      const displayText = customText === "" ? terms.name : customText;
      const topicName = <TopicName>{displayText}</TopicName>;

      switch (typename) {
        case "Newsletter":
          return {
            component: terms?.latestArticleLink ? (
              <TopicNameBaseLink pathname={terms.latestArticleLink}>{topicName}</TopicNameBaseLink>
            ) : (
              topicName
            ),
            name: displayText,
          };
        case "Section":
        case "Topic":
          if (notEmpty(terms))
            return {
              component: (
                <>
                  <TopicNameEntityLink reference={terms}>{topicName}</TopicNameEntityLink>
                  <StyledTopicFollowButton
                    reference={terms}
                    source="Homepage_feature_package"
                    type={typename === "Section" ? FollowType.Section : FollowType.Topic}
                  />
                </>
              ),
              name: displayText,
            };

          return { component: null, name: displayText };
      }
    } else {
      return { component: <TopicName>{customText}</TopicName>, name: customText };
    }
  }, [data?.storyPackageArticles?.refTerms, data?.storyPackageArticles?.settings]);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: renderTopic().name,
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "feature_package",
      },
      subcategory: "widget",
    }),
    [listOfArticles, renderTopic],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    RecirculationWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    getGa4Event: impressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  const handleClick = (entityId?: string, destinationUrl?: string) => {
    sendGATracking({
      action: "Click/feature_package",
      category: "Homepage Depth",
      label: entityId,
    });

    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        block_section: renderTopic().name,
        destination_url: getAbsoluteUrl(destinationUrl),
        page_type: "homepage",
        widget_name: "feature_package",
      },
      subcategory: "widget",
    });
  };

  const articleEdges = data?.storyPackageArticles?.items?.edges ?? [];

  const [leadArticleEdge, ...tailArticleEdges] = articleEdges;
  const leadArticle = leadArticleEdge?.node;

  if (!leadArticle) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.StoryPackage,
      }}
    >
      <Container ref={captureTrackImpressionEventTargetElement}>
        <TopicBadge>{renderTopic().component}</TopicBadge>

        <Articles>
          <StyledContentItemHomeStoryPackageLead reference={leadArticle} onClick={handleClick}>
            {tailArticleEdges.map(({ node }, index) => (
              <StyledContentItemHomeStoryPackageTail
                key={index}
                reference={node}
                onClick={(entityId, urlAlias) => handleClick(entityId, urlAlias)}
              />
            ))}
          </StyledContentItemHomeStoryPackageLead>
        </Articles>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeStoryPackage.displayName = "HomeStoryPackage";
